import {
    always,
    chain,
    compose,
    curry,
    filter,
    identity,
    ifElse,
    invoker,
    isEmpty,
    isNil,
    map,
    prop,
} from 'ramda';
import {
    getXmlDateTime,
    isDate,
    parseXmlDate2,
} from '../../../utility/date.helpers';
import {
    sharePointListItemAttributeXPathQRulesLastModified,
    updateFormItemList,
} from '../helpers/listSubmit.helpers';
import { getNodes, setNodeValueAsync } from '../helpers/qRules.node.helpers';
import { getIdPath } from '../helpers/sharePointMappingSource';
import refreshListItemsProcessor from './RefreshSharePointListItems.processor';

const invalidListIdXpath = 'Failed to select node for SharePoint List Id: {0}';
const unableToFindLastModifiedAtt =
    'Unable to find qRulesLastModified attribute.';
const errorFailedToRefresh = 'Failed to refresh list items: {0}';
const refreshSharePointListitemsNothingToRefresh =
    'No list items to refresh. Verify parameters are correct.';

function selectNodesToRefresh(baseNodes, mapping) {
    var nodePath = mapping.hasBasePath ? mapping.basePath : '.';
    var nodesToRefresh = [];

    baseNodes.forEach(function (baseNode) {
        var selectedNodes = getNodes(baseNode, nodePath);

        nodesToRefresh = nodesToRefresh.concat(selectedNodes);
    });

    return nodesToRefresh;
}

var selectNodes = curry((path, node) => getNodes(node, path));

function checkIdNode(idPath, nodes) {
    //verify that id param will get a node - case 33504
    var idNodes = chain(selectNodes(idPath), nodes);

    if (isEmpty(idNodes)) {
        throw new Error(String.format(invalidListIdXpath, idPath));
    }

    var lmAttNodes = chain(
        selectNodes(sharePointListItemAttributeXPathQRulesLastModified),
        idNodes,
    );

    if (lmAttNodes.length !== idNodes.length) {
        throw new Error(unableToFindLastModifiedAtt);
    }
}

function refresher(mainDataSource, shpAccess) {
    let listRefreshReport = [];
    let refreshErrors = [];

    function logMappingNotice(mapping, messageFormat) {
        var namePart = mapping.mappingName
            ? String.format('"{0}"', mapping.mappingName)
            : '';

        listRefreshReport.push(String.format(messageFormat, namePart));
    }

    function noteSkippedMappingNoId(mapping) {
        logMappingNotice(
            mapping,
            'Skipping mapping {0}. No ID XPath specified.\n',
        );
    }

    function noteSkippedMappingNoItems(mapping) {
        logMappingNotice(mapping, 'Skipping mapping {0}. No nodes to refresh.');
    }

    function getMappedNodesIds(mappedNodes, idPath) {
        return mappedNodes
            .map(invoker(1, 'evaluateString')(idPath))
            .filter(identity);
    }

    // [MappingField] -> [String]
    var getFieldNamesFromMappingFields = compose(
        filter(identity),
        map(ifElse(prop('isId'), always('ID'), prop('sharePointColumn'))),
    );

    function getItems(mapping, mappedNodes) {
        const ln = mapping.listName;
        const allIds = getMappedNodesIds(mappedNodes, mapping.listIdPath);
        const fields = getFieldNamesFromMappingFields(mapping.getFields());
        const allQueryFields = fields.includes('Modified')
            ? fields
            : fields.concat('Modified');
        const filterFields = [
            {
                field: {
                    internalName: 'ID',
                    type: 'Number',
                },
                values: allIds,
            },
        ];

        return shpAccess
            .querySpListAsync({
                listId: ln.useName ? null : ln.id,
                listName: ln.useName ? ln.name : null,
                fields: allQueryFields,
                filterFields,
                siteUrl: mapping.listUrl,
            })
            .then(prop('items'));
    }

    function getIdOrNull(node, idPath) {
        var idNode = node.selectSingle(idPath);
        if (!idNode) {
            return null;
        }

        return parseInt(idNode.value());
    }

    function getNodesWithIds(nodes, idXPath) {
        var nodesWithIds = nodes.map(function (nodeItem) {
            return {
                id: getIdOrNull(nodeItem, idXPath),
                node: nodeItem,
            };
        });

        return nodesWithIds.filter(function (node) {
            return node.id;
        });
    }

    function getItemToUpdate(availableNodes, shpItemId) {
        var filteredNodes = availableNodes.filter(function (node) {
            return node.id === shpItemId;
        });

        return filteredNodes.length > 0 ? filteredNodes[0].node : null;
    }

    // 2024-06-03 - Removed unimplemented rich text handling

    const updateRichTextField = async () => {};

    function getValueToUse(processor, fieldValue) {
        if (!fieldValue) {
            return '';
        }

        if (isDate(fieldValue)) {
            return getXmlDateTime(processor.getSimpleValue(fieldValue));
        }

        if (processor.isLookup(fieldValue)) {
            return processor.getLookupValue(fieldValue);
        }

        if (processor.isPerson(fieldValue)) {
            throw new Error(
                'Refreshing person columns is not currently supported.',
            );
        }

        if (processor.isBoolean(fieldValue)) {
            return processor.getBooleanValueString(fieldValue);
        }

        return processor.getSimpleValue(fieldValue);
    }

    function updateFieldDataAsync(
        processor,
        targetField,
        sourceItem,
        columnName,
    ) {
        var originalValue = processor.getField(columnName, sourceItem);
        var value = getValueToUse(processor, originalValue);

        return setNodeValueAsync(targetField, isNil(value) ? '' : value);
    }

    async function checkFieldForUpdateAsync(
        processor,
        shpItemId,
        shpListItem,
        itemToUpdate,
        fieldDefinition,
    ) {
        const fieldXPath = fieldDefinition.xPath;
        const columnName = fieldDefinition.sharePointColumn;
        const isRichText = fieldDefinition.isRichText;
        const isId = fieldDefinition.isId;

        const isUpdatableField = fieldXPath && columnName && !isId;

        if (!isUpdatableField) {
            return;
        }

        const targetField = itemToUpdate.selectSingle(fieldXPath);

        if (!targetField) {
            return;
        }

        const sourceField = processor.hasField(shpListItem, columnName);

        if (!sourceField) {
            listRefreshReport.push(
                `Warning: The field '${columnName}' was not found in the list.`,
            );
        } else if (isRichText) {
            await updateRichTextField(
                processor,
                shpItemId,
                targetField,
                shpListItem,
                columnName,
            );
        } else {
            await updateFieldDataAsync(
                processor,
                targetField,
                shpListItem,
                columnName,
            );
        }
    }

    async function updateFields(
        processor,
        fields,
        shpListItem,
        itemId,
        itemToUpdate,
    ) {
        for (const field of fields) {
            await checkFieldForUpdateAsync(
                processor,
                itemId,
                shpListItem,
                itemToUpdate,
                field,
            );
        }
    }

    function updateLastModified(idXPath, value, itemToUpdate) {
        var lastModField = itemToUpdate.selectSingle(
            idXPath + '/' + sharePointListItemAttributeXPathQRulesLastModified,
        );

        if (lastModField) {
            return lastModField.setValueAsync(
                getXmlDateTime(value, { separator: ' ' }),
            );
        }

        refreshErrors.push(unableToFindLastModifiedAtt);
        return Promise.resolve();
    }

    async function handleNoLongerPresentItemAsync(
        settings,
        mapping,
        nodeWithIds,
        idXPath,
        shpItemId,
    ) {
        listRefreshReport.push(
            String.format(
                'Item {0} is no longer present in SharePoint List. ',
                shpItemId,
            ),
        );

        if (settings.isUpdate) {
            var item = getItemToUpdate(nodeWithIds, shpItemId);

            if (item) {
                if (mapping.isRepeating) {
                    item.deleteSelf();
                } else {
                    await setNodeValueAsync(
                        item.selectSingle(idXPath),
                        '',
                    ).then(function () {
                        listRefreshReport.push(
                            'Delete not available for non-repeating items. Id field has been cleared. ',
                        );
                    });
                }
            } else {
                throw new Error(
                    String.format(
                        'Unexpected error. Could not find item to delete with ID "{0}".',
                        shpItemId,
                    ),
                );
            }
        }
    }

    // ([RefreshSharePointItem], Number) -> ()
    function checkForAllMatch(shpItems, matchCount) {
        if (matchCount > 0) {
            listRefreshReport.push(
                matchCount === shpItems.length
                    ? 'All items match. '
                    : String.format('{0} matching items found. ', matchCount),
            );
        }
    }

    function getTargetItemModified(item) {
        var parsed = parseXmlDate2(item.modified, { mode: 'local' });

        return parsed?.date ?? null;
    }

    function processItem(
        settings,
        processor,
        shpListItem,
        item,
        nodesWithIds,
        fields,
        idXPath,
    ) {
        var listItemModified = processor.getModifiedDateTime(shpListItem);
        var currentItemModified = getTargetItemModified(item);

        if (currentItemModified < listItemModified) {
            listRefreshReport.push(
                String.format('Item {0} does not match', item.id),
            );

            if (settings.isUpdate) {
                var itemToUpdate = getItemToUpdate(nodesWithIds, item.id);

                if (itemToUpdate) {
                    return updateFields(
                        processor,
                        fields,
                        shpListItem,
                        item.id,
                        itemToUpdate,
                    )
                        .then(function () {
                            return updateLastModified(
                                idXPath,
                                listItemModified,
                                itemToUpdate,
                            );
                        })
                        .then(function () {
                            return true;
                        });
                }
            }

            return Promise.resolve(true);
        }

        return Promise.resolve(false);
    }

    // (String, RefreshCommandSettings, Mapping, Processor, [DataSourceNodes]) -> Promise
    async function processChanges(
        idXPath,
        settings,
        mapping,
        processor,
        mappedNodes,
    ) {
        var shpItems = mapping.baseNode.selectNodes(
            'SharePointItems/SharePointItem',
        );
        var fields = mapping.getFields();
        var mappingName = mapping.mappingName;

        if (mappingName) {
            listRefreshReport.push(
                String.format('Report for mapping: {0}', mappingName),
            );
        }

        var nodesWithIds = getNodesWithIds(mappedNodes, idXPath);
        var matchCount = 0;
        var items = shpItems.map(function (shpItem) {
            return {
                id: parseInt(shpItem.evaluateString('Id')),
                modified: shpItem.evaluateString('LastModified'),
            };
        });

        for (const item of items) {
            var shpListItem = processor.getItem(item.id);

            if (shpListItem) {
                const changed = await processItem(
                    settings,
                    processor,
                    shpListItem,
                    item,
                    nodesWithIds,
                    fields,
                    idXPath,
                );
                if (!changed) {
                    matchCount += 1;
                }
            } else {
                await handleNoLongerPresentItemAsync(
                    settings,
                    mapping,
                    nodesWithIds,
                    idXPath,
                    item.id,
                );

                if (settings.isUpdate) {
                    const refreshedNodes = selectNodesToRefresh(
                        [mainDataSource], // baseNodes
                        mapping,
                    );

                    await updateFormItemList(refreshedNodes, idXPath, mapping);
                }
            }
        }

        checkForAllMatch(shpItems, matchCount);
    }

    function generateListData(mapping, settings, idXPath, mappedNodes) {
        return getItems(mapping, mappedNodes).then(function (listItems) {
            if (!listItems || listItems.length === 0) {
                throw new Error(
                    String.format(
                        errorFailedToRefresh,
                        refreshSharePointListitemsNothingToRefresh,
                    ),
                );
            }

            return processChanges(
                idXPath,
                settings,
                mapping,
                refreshListItemsProcessor(listItems),
                mappedNodes,
            );
        });
    }

    async function processChildMappings(settings, mapping, mappedNodes) {
        const childMappings = settings.mappings.getChildMappings(mapping);

        for (const childMapping of childMappings) {
            // eslint-disable-next-line no-use-before-define -- processChildMappings() and refreshSharePointListItems() call each other
            await refreshSharePointListItems(
                settings,
                childMapping,
                mappedNodes,
            );
        }
    }

    async function refreshSharePointListItems(settings, mapping, baseNodes) {
        const idXPath = getIdPath(mapping, settings.idxPath);

        if (!idXPath) {
            noteSkippedMappingNoId(mapping);

            return;
        }

        const mappedNodes = selectNodesToRefresh(baseNodes, mapping);

        if (!mappedNodes || mappedNodes.length === 0) {
            noteSkippedMappingNoItems(mapping);

            // BUGBUG - Should we return here?
        }

        checkIdNode(idXPath, mappedNodes);

        try {
            await updateFormItemList(mappedNodes, idXPath, mapping);

            await generateListData(mapping, settings, idXPath, mappedNodes);

            await processChildMappings(settings, mapping, mappedNodes);
        } catch (e) {
            console.error(e);
            refreshErrors.push(String.format(errorFailedToRefresh, e.message));
        }
    }

    return {
        listRefreshReport,
        refreshErrors,
        refreshSharePointListItems,
    };
}

export default refresher;
